import React, { memo } from 'react';
import { Box, Text } from '@chakra-ui/react';

/**
 * @param {object} props
 * @param {number} props.index
 * @param {'X' | 'O' | null} props.value
 * @param {boolean} props.isWinning
 * @param {boolean} props.disabled
 * @param {boolean} props.showGhost
 * @param {'X' | 'O' | null} props.ghostPlayer
 * @param {boolean} props.isActivePlayerCell
 * @param {(i: number) => void} props.onCellClick
 * @param {(i: number | null) => void} props.onHoverChange
 */
function TicTacToeCell({
  index,
  value,
  isWinning,
  disabled,
  showGhost,
  ghostPlayer,
  isActivePlayerCell,
  onCellClick,
  onHoverChange,
}) {
  const playable = !disabled && value === null;

  return (
    <Box
      as="button"
      type="button"
      aria-label={
        value
          ? `Cell ${index + 1}, ${value}`
          : playable
            ? `Cell ${index + 1}, empty. Press to place mark.`
            : `Cell ${index + 1}, empty`
      }
      position="relative"
      aspectRatio="1"
      minH={{ base: '88px', sm: '100px', md: '112px' }}
      maxH="min(28vw, 132px)"
      w="100%"
      borderRadius="xl"
      borderWidth="1px"
      borderColor={
        isWinning
          ? 'teal.300'
          : isActivePlayerCell
            ? 'whiteAlpha.200'
            : 'whiteAlpha.080'
      }
      bg={
        isWinning
          ? 'linear-gradient(145deg, rgba(45, 212, 191, 0.22) 0%, rgba(13, 148, 136, 0.12) 100%)'
          : 'rgba(255, 255, 255, 0.04)'
      }
      boxShadow={
        isWinning
          ? '0 0 0 1px rgba(45, 212, 191, 0.35), 0 8px 28px rgba(13, 148, 136, 0.2)'
          : 'inset 0 1px 0 rgba(255,255,255,0.06)'
      }
      transition="transform 0.11s ease-out, border-color 0.11s ease-out, background 0.11s ease-out, box-shadow 0.18s ease-out"
      cursor={playable ? 'pointer' : 'default'}
      _hover={
        playable
          ? {
              bg: 'rgba(255, 255, 255, 0.085)',
              borderColor: 'whiteAlpha.280',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px -4px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
            }
          : undefined
      }
      _active={playable ? { transform: 'translateY(0) scale(0.98)' } : undefined}
      _focusVisible={{
        outline: '2px solid',
        outlineColor: 'teal.400',
        outlineOffset: '2px',
      }}
      disabled={!playable}
      onClick={() => playable && onCellClick(index)}
      onMouseEnter={() => playable && onHoverChange(index)}
      onMouseLeave={() => onHoverChange(null)}
      onFocus={() => playable && onHoverChange(index)}
      onBlur={() => onHoverChange(null)}
      sx={{
        '@keyframes ttt-pop': {
          '0%': { opacity: 0, transform: 'scale(0.82)' },
          '100%': { opacity: 1, transform: 'scale(1)' },
        },
        '@keyframes ttt-win-pulse': {
          '0%, 100%': { boxShadow: '0 0 0 1px rgba(45, 212, 191, 0.32), 0 6px 22px rgba(13, 148, 136, 0.16)' },
          '50%': { boxShadow: '0 0 0 1px rgba(94, 234, 212, 0.45), 0 10px 28px rgba(45, 212, 191, 0.22)' },
        },
        ...(isWinning
          ? { animation: 'ttt-win-pulse 2s ease-in-out infinite' }
          : {}),
      }}
    >
      {value ? (
        <Text
          as="span"
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize={{ base: '2.75rem', md: '3.25rem' }}
          fontWeight="800"
          letterSpacing="-0.04em"
          lineHeight={1}
          color={value === 'X' ? 'cyan.200' : 'orange.200'}
          textShadow="0 2px 24px rgba(0,0,0,0.35)"
          sx={{
            animation: 'ttt-pop 0.14s cubic-bezier(0.16, 1, 0.3, 1) forwards',
          }}
          userSelect="none"
          aria-hidden
        >
          {value}
        </Text>
      ) : showGhost && ghostPlayer ? (
        <Text
          as="span"
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize={{ base: '2.75rem', md: '3.25rem' }}
          fontWeight="800"
          letterSpacing="-0.04em"
          lineHeight={1}
          color={ghostPlayer === 'X' ? 'cyan.400' : 'orange.300'}
          opacity={0.22}
          pointerEvents="none"
          userSelect="none"
          aria-hidden
        >
          {ghostPlayer}
        </Text>
      ) : null}
    </Box>
  );
}

export default memo(TicTacToeCell);
